<template>
    <v-container fluid class="pa-4">
        <v-card>
            <v-card-title class="text-h6">
                <v-icon left color="primary">mdi-history</v-icon>
                Cronologia modifiche
                <v-spacer></v-spacer>
                <v-btn icon @click="load(1)" :loading="loading">
                    <v-icon>mdi-refresh</v-icon>
                </v-btn>
            </v-card-title>

            <v-card-text>
                <v-alert v-if="!loading && items.length === 0" type="info" dense text>
                    Nessuna modifica tracciata per questo dipendente.
                </v-alert>

                <v-data-table
                    v-else
                    :headers="headers"
                    :items="items"
                    :loading="loading"
                    :server-items-length="meta.total"
                    :options.sync="options"
                    :footer-props="{ itemsPerPageOptions: [25, 50, 100, 200] }"
                    dense
                    class="elevation-0">
                    <template v-slot:item.created_at="{ item }">
                        <span class="text-caption">{{ formatDateTime(item.created_at) }}</span>
                    </template>
                    <template v-slot:item.event="{ item }">
                        <v-chip x-small :color="eventColor(item.event)" outlined>{{ eventLabel(item.event) }}</v-chip>
                    </template>
                    <template v-slot:item.auditable_short="{ item }">
                        <span class="font-weight-medium">{{ entityLabel(item.auditable_short) }}</span>
                        <span class="text-caption grey--text"> #{{ item.auditable_id }}</span>
                    </template>
                    <template v-slot:item.user_name="{ item }">
                        <span v-if="item.user_name">{{ item.user_name }}</span>
                        <span v-else class="grey--text">—</span>
                    </template>
                    <template v-slot:item.changes="{ item }">
                        <v-btn
                            v-if="hasChanges(item)"
                            x-small
                            text
                            color="primary"
                            @click="showDiff(item)">
                            <v-icon left small>mdi-eye</v-icon>
                            {{ countChanges(item) }} campi
                        </v-btn>
                        <span v-else class="grey--text text-caption">—</span>
                    </template>
                </v-data-table>
            </v-card-text>
        </v-card>

        <v-dialog v-model="diffDialog" max-width="700" scrollable>
            <v-card v-if="selectedItem">
                <v-card-title class="text-h6">
                    Modifiche {{ entityLabel(selectedItem.auditable_short) }} #{{ selectedItem.auditable_id }}
                    <v-spacer></v-spacer>
                    <v-btn icon @click="diffDialog = false"><v-icon>mdi-close</v-icon></v-btn>
                </v-card-title>
                <v-divider></v-divider>
                <v-card-text class="pa-4">
                    <div class="text-caption mb-2">
                        {{ formatDateTime(selectedItem.created_at) }}
                        <span v-if="selectedItem.user_name"> · {{ selectedItem.user_name }}</span>
                    </div>
                    <v-simple-table dense>
                        <thead>
                            <tr>
                                <th>Campo</th>
                                <th>Da</th>
                                <th>A</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="key in diffKeys(selectedItem)" :key="key">
                                <td class="font-weight-medium">{{ key }}</td>
                                <td class="text-caption red--text text--darken-1">{{ formatValue(selectedItem.old_values && selectedItem.old_values[key]) }}</td>
                                <td class="text-caption green--text text--darken-1">{{ formatValue(selectedItem.new_values && selectedItem.new_values[key]) }}</td>
                            </tr>
                        </tbody>
                    </v-simple-table>
                </v-card-text>
            </v-card>
        </v-dialog>
    </v-container>
</template>

<script>
export default {
    name: 'hr-employee-audit',
    props: {
        id: { type: Number, required: true },
    },

    data() {
        return {
            loading: false,
            items: [],
            meta: { total: 0, current_page: 1, last_page: 1, per_page: 50 },
            options: { page: 1, itemsPerPage: 50 },
            diffDialog: false,
            selectedItem: null,
            headers: [
                { text: 'Quando', value: 'created_at', width: 160 },
                { text: 'Evento', value: 'event', width: 100 },
                { text: 'Entità', value: 'auditable_short' },
                { text: 'Utente', value: 'user_name', width: 200 },
                { text: 'Modifiche', value: 'changes', sortable: false, width: 130 },
            ],
        }
    },

    watch: {
        'options.page'(p) { this.load(p) },
        'options.itemsPerPage'() { this.load(1) },
    },

    created() {
        this.load(1)
    },

    methods: {
        load(page) {
            this.loading = true
            this.axios.get('hremployees/' + this.id + '/audit', {
                params: { page: page || this.options.page, per_page: this.options.itemsPerPage },
            }).then(response => {
                this.items = response.data.data || []
                this.meta = response.data.meta || this.meta
                this.loading = false
            }, () => {
                this.items = []
                this.loading = false
            })
        },
        formatDateTime(s) {
            if (!s) return ''
            const d = new Date(s)
            return d.toLocaleString('it-IT', { dateStyle: 'short', timeStyle: 'medium' })
        },
        eventColor(event) {
            const map = { created: 'success', updated: 'primary', deleted: 'error', restored: 'warning' }
            return map[event] || 'grey'
        },
        eventLabel(event) {
            const map = { created: 'Creato', updated: 'Modificato', deleted: 'Eliminato', restored: 'Ripristinato' }
            return map[event] || event
        },
        entityLabel(shortType) {
            const map = {
                HrEmployee: 'Dipendente',
                HrContract: 'Contratto',
                HrEmployeeShift: 'Turno',
                HrEmployeeSkill: 'Skill',
                HrAbsence: 'Assenza',
                HrPayslip: 'Cedolino',
                HrEmployeeOnboarding: 'Onboarding',
                HrWorkingDay: 'Cartellino',
            }
            return map[shortType] || shortType
        },
        hasChanges(item) {
            return this.countChanges(item) > 0
        },
        countChanges(item) {
            return this.diffKeys(item).length
        },
        diffKeys(item) {
            const keys = new Set()
            if (item.old_values) Object.keys(item.old_values).forEach(k => keys.add(k))
            if (item.new_values) Object.keys(item.new_values).forEach(k => keys.add(k))
            return Array.from(keys).filter(k => k !== 'updated_at' && k !== 'created_at')
        },
        formatValue(v) {
            if (v === null || v === undefined) return '—'
            if (typeof v === 'boolean') return v ? 'sì' : 'no'
            if (typeof v === 'object') return JSON.stringify(v)
            return String(v)
        },
        showDiff(item) {
            this.selectedItem = item
            this.diffDialog = true
        },
    },
}
</script>
